/**
 * useStickyDdlStructure — keeps the compact Visual DDL body MOUNTED across the
 * resolver's async re-parse window.
 *
 * The two parses that feed the compact editor do not land together:
 *  - `useDdlStatementDetection` re-parses the buffer SYNCHRONOUSLY, so the
 *    parked `statementId` (a content hash — `sql-grammar/document/ids.ts`)
 *    flips to its new value on the very next render after a keystroke;
 *  - `useInlineDdlEditContext` pushes the same text through
 *    `service.onTextChange`, which is a PROMISE.
 *
 * In between, `service.extractDdlStructure(documentId, newStatementId)` throws
 * (`requireStatement`: "no statement '<id>' in document"), so a naive
 * "structure ?? fallback" dispatch drops to the read-only card on every
 * keystroke — unmounting `TableEditor` and resetting its tab back to Columns.
 *
 * The fix keys off a precise signal rather than a timer: if the parked
 * statement id is NOT present in the service's parsed buffer, the re-parse is
 * still in flight, so the last known structure is held. Once the buffer DOES
 * contain that id and extraction still fails, the statement genuinely is not
 * an extractable table shape and the cache is dropped so the caller can fall
 * back honestly. Moving the cursor to a different statement does not change
 * the buffer text, so the service is already settled there and the switch is
 * evaluated immediately — no stale body.
 *
 * Redesign: .superpowers/sdd/2026-07-28-compact-visual-ddl-editor/ (T3).
 */
import { useRef } from 'react';

import type { DdlStructure } from '../../../sql-resolver/types/ddlEdit';
import type { VqeDdlEditContext } from '../edit/editContext';
import { structureFromDdlEditContext } from './structureFromDdlEditContext';

/** Is the parked statement id present in the service's CURRENT parsed buffer?
 *  `false` ⇒ the re-parse for the latest keystroke has not landed yet. */
function serviceHasParkedStatement(editContext: VqeDdlEditContext): boolean {
  try {
    const buffer = editContext.service.getParsedBuffer(editContext.documentId);
    return !!buffer?.documentStructure.statements.some(
      (statement) => statement.id === editContext.statementId,
    );
  } catch {
    return false;
  }
}

export function useStickyDdlStructure(
  editContext: VqeDdlEditContext | null,
): DdlStructure | null {
  // Written during render on purpose — this is a render-derived cache, not
  // state: it never triggers a render of its own and is idempotent under a
  // double invocation.
  const lastRef = useRef<{ statementId: string; structure: DdlStructure } | null>(null);

  if (!editContext) {
    lastRef.current = null;
    return null;
  }

  const fresh = structureFromDdlEditContext(editContext);
  if (fresh) {
    lastRef.current = { statementId: editContext.statementId, structure: fresh };
    return fresh;
  }

  // Extraction failed. Hold the previous structure only while the service is
  // demonstrably behind the detection parse.
  if (lastRef.current && !serviceHasParkedStatement(editContext)) {
    return lastRef.current.structure;
  }

  lastRef.current = null;
  return null;
}
